import React, { useEffect, useState, useCallback, useRef } from 'react';
import { FileText, Image as ImageIcon, Music, Video, FileUp, Trash2, Loader, Download, History, FolderPlus, Folder as FolderIcon } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import { formatDate } from '../../utils/formatDate';

const TYPE_ICON = {
  pdf: FileText, doc: FileText, docx: FileText, txt: FileText, md: FileText,
  png: ImageIcon, jpg: ImageIcon, jpeg: ImageIcon, gif: ImageIcon, webp: ImageIcon, svg: ImageIcon,
  mp3: Music, wav: Music, m4a: Music,
  mp4: Video, mov: Video, webm: Video,
};
const CHUNKED_UPLOAD_THRESHOLD_BYTES = 10 * 1024 * 1024; // mirrors DocumentService.CHUNKED_UPLOAD_THRESHOLD_BYTES

function formatBytes(n) {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let i = 0; let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${units[i]}`;
}

/**
 * Drop-in "Documents" panel for any CRM record detail page. Backed by the
 * multi-provider Storage module (src/modules/storage) — files land in
 * whichever cloud provider the company has connected (or local storage by
 * default), auto-filed into a folder created for this specific record on
 * first upload, no pre-provisioning needed by the caller.
 *
 * @param {string} module     one of 'leads' | 'deals' | 'contacts' | 'accounts'
 * @param {string} moduleId   the record's own _id
 * @param {string} recordName human-readable name used for the record's folder (e.g. lead.name, deal.title)
 */
export default function RelatedFiles({ module, moduleId, recordName }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);

  // The record's own root folder (auto-created/reused) plus any subfolders the
  // user has created under it — e.g. "Photos", "Contracts" — so uploads/images
  // can be organized instead of always landing in one flat folder.
  const [rootFolder, setRootFolder] = useState(null);
  const [subfolders, setSubfolders] = useState([]);
  const [activeFolderId, setActiveFolderId] = useState(null); // null = record's root folder
  const [creatingFolder, setCreatingFolder] = useState(false);

  const fetchFiles = useCallback(async (folderId) => {
    try {
      const res = await api.get('/storage/files', { params: { module, moduleId, folderId: folderId || undefined } });
      setFiles(res.data.items || []);
    } catch (err) {
      console.error('Failed to load documents', err);
    } finally {
      setLoading(false);
    }
  }, [module, moduleId]);

  // Ensures the record's own folder exists (idempotent) so subfolders can be
  // created under it even before any file has ever been uploaded, then loads
  // whatever subfolders already exist.
  const fetchFolders = useCallback(async () => {
    try {
      const { data: ensureRes } = await api.post('/storage/folders/ensure-record', { module, moduleId, recordName });
      const root = ensureRes.data;
      setRootFolder(root);
      const { data: listRes } = await api.get('/storage/folders', { params: { module, moduleId } });
      setSubfolders((listRes.data || []).filter(f => String(f.parentFolderId) === String(root._id)));
    } catch (err) {
      console.error('Failed to load folders', err);
    }
  }, [module, moduleId, recordName]);

  useEffect(() => { fetchFiles(activeFolderId); }, [fetchFiles, activeFolderId]);
  useEffect(() => { fetchFolders(); }, [fetchFolders]);

  const handleCreateFolder = async () => {
    const name = await alertService.prompt('New Folder', 'Folder name (e.g. Photos, Contracts, ID Proofs)', { confirmText: 'Create' });
    if (!name || !name.trim()) return;
    setCreatingFolder(true);
    try {
      const { data } = await api.post('/storage/folders', {
        name: name.trim(), parentFolderId: rootFolder?._id, module, moduleId,
      });
      setSubfolders(prev => [...prev, data.data]);
      alertService.toast('success', `Folder "${name.trim()}" created`);
    } catch (err) {
      alertService.error('Error', err.response?.data?.error || 'Failed to create folder.');
    } finally {
      setCreatingFolder(false);
    }
  };

  const uploadChunked = async (file) => {
    const { data } = await api.post('/storage/upload/session', {
      fileName: file.name, mimeType: file.type, totalSize: file.size, module, moduleId, recordName,
      folderId: activeFolderId || undefined,
    });
    const { sessionId, chunkSize } = data.data;
    let offset = 0;

    while (offset < file.size) {
      const chunk = await file.slice(offset, Math.min(offset + chunkSize, file.size)).arrayBuffer();
      let attempt = 0;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          const res = await api.put(`/storage/upload/session/${sessionId}/chunk`, chunk, { headers: { 'Content-Type': 'application/octet-stream' } });
          if (res.data.done) return;
          offset = res.data.bytesUploaded;
          setUploadProgress(Math.round((offset / file.size) * 100));
          break;
        } catch (err) {
          attempt += 1;
          if (attempt >= 3) {
            await api.delete(`/storage/upload/session/${sessionId}`).catch(() => {});
            throw err;
          }
          await new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
        }
      }
    }
  };

  const uploadFiles = async (fileList) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    setUploading(true);
    setUploadProgress(0);
    try {
      for (const file of list) {
        if (file.size > CHUNKED_UPLOAD_THRESHOLD_BYTES) {
          await uploadChunked(file);
        } else {
          const form = new FormData();
          form.append('file', file);
          form.append('module', module);
          form.append('moduleId', moduleId);
          form.append('recordName', recordName || '');
          if (activeFolderId) form.append('folderId', activeFolderId);
          await api.post('/storage/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      }
      alertService.toast('success', list.length > 1 ? `${list.length} files uploaded` : 'File uploaded');
      fetchFiles(activeFolderId);
    } catch (err) {
      alertService.error('Upload Failed', err.response?.data?.error || 'Failed to upload file.');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInput = (e) => { uploadFiles(e.target.files); e.target.value = ''; };

  // Drag counter, not a boolean, because dragenter/dragleave fire once per descendant
  // element as the pointer crosses child boundaries — a plain boolean flickers dragActive
  // off mid-drag every time it passes over a nested element.
  const onDragEnter = (e) => { e.preventDefault(); dragCounter.current += 1; setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); dragCounter.current -= 1; if (dragCounter.current <= 0) { dragCounter.current = 0; setDragActive(false); } };
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = (e) => {
    e.preventDefault();
    dragCounter.current = 0;
    setDragActive(false);
    uploadFiles(e.dataTransfer.files);
  };

  const handleDelete = async (fileId) => {
    const confirmed = await alertService.confirm('Delete File?', 'This permanently deletes the file and its version history.', 'warning', 'Delete');
    if (!confirmed) return;
    try {
      await api.delete(`/storage/files/${fileId}`);
      alertService.toast('success', 'File deleted');
      fetchFiles(activeFolderId);
    } catch (err) {
      alertService.error('Error', 'Failed to delete file.');
    }
  };

  if (loading) {
    return <div className="py-8 flex justify-center"><Loader size={20} className="animate-spin text-[#0176D3]" /></div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold text-[#706E6B] uppercase tracking-widest">Documents ({files.length})</p>
        <div className="flex items-center gap-2">
          <button
            onClick={handleCreateFolder}
            disabled={creatingFolder || !rootFolder}
            title="Create a subfolder in your connected storage (ImageKit by default)" aria-label="Create a subfolder in your connected storage (ImageKit by default)"
            className="h-8 px-3 border border-[#D8DDE6] rounded text-[13px] font-semibold text-[#080707] hover:bg-[#F3F2F2] disabled:opacity-50 flex items-center gap-1.5 whitespace-nowrap shrink-0"
          >
            {creatingFolder ? <Loader size={13} className="animate-spin" /> : <FolderPlus size={13} />}
            New Folder
          </button>
          <label className="h-8 px-3 bg-[#0176D3] text-white rounded text-[12px] font-bold hover:bg-[#014486] cursor-pointer flex items-center gap-1.5">
            {uploading ? <Loader size={13} className="animate-spin" /> : <FileUp size={13} />}
            {uploading ? (uploadProgress > 0 ? `Uploading… ${uploadProgress}%` : 'Uploading…') : 'Upload'}
            <input type="file" multiple accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.mp3,.mp4,.mov,.wav" className="hidden" onChange={handleFileInput} disabled={uploading} />
          </label>
        </div>
      </div>

      {subfolders.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          <button
            onClick={() => setActiveFolderId(null)}
            className={`h-7 px-2.5 rounded text-[11px] font-bold flex items-center gap-1 transition-colors ${!activeFolderId ? 'bg-[#0176D3] text-white' : 'bg-[#F4F6F9] text-[#706E6B] hover:bg-[#EAF4FE]'}`}
          >
            <FolderIcon size={11} /> All
          </button>
          {subfolders.map(folder => (
            <button
              key={folder._id}
              onClick={() => setActiveFolderId(folder._id)}
              className={`h-7 px-2.5 rounded text-[11px] font-bold flex items-center gap-1 transition-colors ${activeFolderId === folder._id ? 'bg-[#0176D3] text-white' : 'bg-[#F4F6F9] text-[#706E6B] hover:bg-[#EAF4FE]'}`}
            >
              <FolderIcon size={11} /> {folder.name}
            </button>
          ))}
        </div>
      )}

      <div
        onDragEnter={onDragEnter} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed transition-colors ${dragActive ? 'border-[#0176D3] bg-[#EEF7FF]' : 'border-[#D8DDE6]'}`}
      >
        {files.length === 0 ? (
          <div className="py-8 text-center text-[12px] text-[#706E6B]">
            {dragActive ? 'Drop to upload' : 'Drag files or images here, or click Upload above.'}
          </div>
        ) : (
          <div className="p-2 space-y-2">
            {dragActive && (
              <div className="py-3 text-center text-[12px] font-semibold text-[#0176D3]">Drop to upload</div>
            )}
            {files.map(file => {
              const isImage = (file.mimeType || '').startsWith('image/') && file.previewUrl;
              const Icon = TYPE_ICON[file.extension] || FileText;
              return (
                <div key={file._id} className="flex items-center gap-3 p-3 bg-[#F4F6F9] border border-[#D8DDE6] rounded-lg">
                  {isImage ? (
                    <img src={file.previewUrl} alt={file.fileName} className="w-8 h-8 rounded object-cover border border-[#D8DDE6] shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-white border border-[#E5E5E5] flex items-center justify-center shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
                      <Icon size={14} className="text-[#0176D3]" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[12px] font-bold text-[#080707] truncate">{file.fileName}</p>
                    <p className="text-[10px] text-[#706E6B] flex items-center gap-1">
                      {file.currentVersion > 1 && <span className="inline-flex items-center gap-0.5"><History size={9} />v{file.currentVersion} · </span>}
                      {formatBytes(file.size)} · {formatDate(file.createdAt)}
                    </p>
                  </div>
                  <a
                    // ImageKit/local_storage files have a real, directly-public CDN URL
                    // (file.downloadUrl) — link straight to that instead of proxying
                    // through our own server. Providers whose files aren't publicly
                    // reachable (no downloadUrl stored) still fall back to the
                    // authenticated proxy endpoint.
                    href={file.downloadUrl || `/api/storage/files/${file._id}/download`}
                    target={file.downloadUrl ? '_blank' : undefined}
                    rel={file.downloadUrl ? 'noopener noreferrer' : undefined}
                    download={file.downloadUrl ? file.fileName : undefined}
                    title="Download"
                    className="p-1.5 text-[#706E6B] hover:text-[#0176D3] hover:bg-white rounded transition-colors"
                  >
                    <Download size={14} />
                  </a>
                  <button onClick={() => handleDelete(file._id)} title="Delete" className="p-1.5 text-[#BA0517] hover:bg-[#FDE7E9] rounded transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
